import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import Shop from "./pages/Shop";
import Reel from "./pages/Reel";
import ProductDetail from "./pages/ProductDetail";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import AdminDesignStudio from "./pages/AdminDesignStudio";
import AdminLogoStudio from "./pages/AdminLogoStudio";
import AdminOrders from "./pages/AdminOrders";
import AdminFinance from "./pages/AdminFinance";
import AdminDiscounts from "./pages/AdminDiscounts";
import AdminAbandoned from "./pages/AdminAbandoned";
import ShowCard from "./pages/ShowCard";
import ManufacturerSpec from "./pages/ManufacturerSpec";
import ManufacturerSpecAll from "./pages/ManufacturerSpecAll";
import Cart from "./pages/Cart";
import CustomerLogin from "./pages/CustomerLogin";
import AuthCallback from "./pages/AuthCallback";
import MyAccount from "./pages/MyAccount";
import { AboutUs, PrivacyPolicy, TermsConditions, Returns, Shipping, Contact } from "./pages/InfoPages";
import { getAdminToken } from "./lib/api";
import { CustomerProvider } from "./contexts/CustomerContext";
import ErrorBoundary from "./components/ErrorBoundary";

const RequireAdmin = ({ children }) => {
  const token = getAdminToken();
  if (!token) return <Navigate to="/admin/login" replace />;
  return children;
};

function App() {
  useEffect(() => {
    const url = `${process.env.PUBLIC_URL || ""}/bg-texture.jpeg`;
    // Tile the gold diamond-plate texture at a small fixed size so the diamonds
    // stay sharp and detailed on every screen, instead of `cover`-stretching
    // which zooms into flat gaps and makes the background look like blurred dots.
    document.body.style.backgroundImage = `url('${url}')`;
    document.body.style.backgroundRepeat = "repeat";
    document.body.style.backgroundSize = "340px 340px";
    document.body.style.backgroundPosition = "top left";
    document.body.style.backgroundAttachment = "fixed";
    document.body.style.backgroundColor = "#0a0a0a";
  }, []);

  return (
    <div className="App noise min-h-screen text-white">
      {/* Faint RAW COMPANY tailor-mark watermark — fixed to the viewport, behind all content */}
      <div
        aria-hidden="true"
        data-testid="brand-watermark"
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 0,
          pointerEvents: "none",
          backgroundImage: "url(/raw-lion-watermark.png?v=4)",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center 32%",
          backgroundSize: "min(95vw, 780px) auto",
          opacity: 0.18,
          mixBlendMode: "screen",
          filter: "saturate(1.3) brightness(1.15)",
        }}
      />
      <ErrorBoundary>
        <BrowserRouter>
          <CustomerProvider>
          <Routes>
          <Route path="/" element={<Shop />} />
          <Route path="/showcards/rawcompanyclothing" element={<ShowCard />} />
          <Route path="/showcards/rawcompanyclothing/" element={<ShowCard />} />
          <Route path="/spec/:shortId" element={<ManufacturerSpec />} />
          <Route path="/spec/:shortId/" element={<ManufacturerSpec />} />
          <Route path="/spec-all" element={<ManufacturerSpecAll />} />
          <Route path="/spec-all/" element={<ManufacturerSpecAll />} />
          <Route path="/reel" element={<Reel />} />
          <Route path="/product/:id" element={<ProductDetail />} />
          <Route path="/success" element={<CheckoutSuccess />} />
          <Route path="/cart" element={<Cart />} />

          {/* Customer auth */}
          <Route path="/login" element={<CustomerLogin />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/account" element={<MyAccount />} />

          <Route path="/about" element={<AboutUs />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsConditions />} />
          <Route path="/returns" element={<Returns />} />
          <Route path="/shipping" element={<Shipping />} />
          <Route path="/contact" element={<Contact />} />

          <Route path="/admin/login" element={<AdminLogin />} />
          <Route
            path="/admin"
            element={
              <RequireAdmin>
                <AdminDashboard />
              </RequireAdmin>
            }
          />
          <Route
            path="/admin/orders"
            element={
              <RequireAdmin>
                <AdminOrders />
              </RequireAdmin>
            }
          />
          <Route
            path="/admin/finance"
            element={
              <RequireAdmin>
                <AdminFinance />
              </RequireAdmin>
            }
          />
          <Route
            path="/admin/discounts"
            element={
              <RequireAdmin>
                <AdminDiscounts />
              </RequireAdmin>
            }
          />
          <Route
            path="/admin/abandoned"
            element={
              <RequireAdmin>
                <AdminAbandoned />
              </RequireAdmin>
            }
          />
          <Route
            path="/admin/logo"
            element={
              <RequireAdmin>
                <AdminLogoStudio />
              </RequireAdmin>
            }
          />
          <Route
            path="/admin/design/new"
            element={
              <RequireAdmin>
                <AdminDesignStudio />
              </RequireAdmin>
            }
          />
          <Route
            path="/admin/design/:id"
            element={
              <RequireAdmin>
                <AdminDesignStudio />
              </RequireAdmin>
            }
          />
        </Routes>
        </CustomerProvider>
      </BrowserRouter>
      </ErrorBoundary>
      <Toaster theme="dark" position="top-right" richColors closeButton />
    </div>
  );
}

export default App;
